import { AppleButton, AppleButtonProps } from '@sparemin/blockhead';
import { noop, over } from 'lodash-es';
import React from 'react';
import { useSocialAuth } from 'api';
import { useAppleLogin, UseAppleLoginConfig } from 'oauth';
import { DEFAULT_APPLE_LOGIN_ERROR_MESSAGE } from 'ui/constants';
import { useAuth } from 'ui/context/AuthContext';
import { SocialAuthSuccessResponse } from 'ui/types';
import { ensureAuthResponse } from 'ui/utils';

export interface AppleSignInButtonProps
  extends Pick<AppleButtonProps, 'isDisabled' | 'onPress'>,
    Pick<UseAppleLoginConfig, 'onCancel'> {
  /**
   * Called when there is an authentication error
   */
  onError?: (message: string) => void;
  /**
   * Called when auth is successful
   */
  onSuccess?: (response: SocialAuthSuccessResponse) => void;
}

/**
 * Button to sign-in to Headliner using Apple authentication
 */
const AppleSignInButton: React.FC<AppleSignInButtonProps> = ({
  isDisabled,
  onCancel,
  onError: onErrorProp,
  onPress = noop,
  onSuccess: onSuccessProp,
}) => {
  const { mutate: socialAuth } = useSocialAuth();
  const { promotionKey, referralKey, traceId } = useAuth();
  const { authenticate } = useAppleLogin({
    onCancel,
    onError() {
      onErrorProp?.(DEFAULT_APPLE_LOGIN_ERROR_MESSAGE);
    },
    onSuccess(data) {
      socialAuth(
        {
          promotionKey,
          referralKey,
          traceId,
          authenticationServiceToken: data.authorization.id_token,
          identityProvider: 'apple',
        },
        {
          onSuccess(res) {
            // guard against critical auth data that might be missing
            try {
              onSuccessProp?.(ensureAuthResponse(res));
            } catch {
              onErrorProp?.(DEFAULT_APPLE_LOGIN_ERROR_MESSAGE);
            }
          },
          onError() {
            onErrorProp?.(DEFAULT_APPLE_LOGIN_ERROR_MESSAGE);
          },
        },
      );
    },
  });

  return (
    <AppleButton
      isDisabled={isDisabled}
      onPress={over([authenticate, onPress])}
    />
  );
};

export default AppleSignInButton;
